import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { XCircle, Wrench, RefreshCw, Link2, Users } from "lucide-react";
import { useSlipNameLinks, LinkSlipSelect, SavedLinksList } from "@/components/SlipNameLinks";

const _norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const MatchHints = ({ d, i }) => {
  if (d.slip_product === undefined) return null;   // older entry — no hint data
  return (
    <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] leading-4">
      {d.slip_product
        ? (_norm(d.slip_product) !== _norm(d.product) && (
          <span className="rounded bg-sky-50 text-sky-700 border border-sky-200 px-1.5" title="File product was matched to this slip product name"
                data-testid={`qty-mismatch-slip-name-${i}`}>
            <Link2 className="inline w-3 h-3 mr-0.5 -mt-0.5" />Slip: {d.slip_product}
          </span>))
        : (
          <span className="rounded bg-rose-50 text-rose-700 border border-rose-200 px-1.5" data-testid={`qty-mismatch-not-on-slip-${i}`}>
            Not found on slip
          </span>)}
      {d.pooled && (
        <span className="rounded bg-amber-50 text-amber-800 border border-amber-200 px-1.5"
              title={`One slip line of ${d.pool_total} is shared by ${d.pool_customers} customers (file total ${d.pool_file_total}). Each customer takes their file qty in order; the last gets what is left — this row's Slip Qty is its share.`}
              data-testid={`qty-mismatch-pooled-${i}`}>
          <Users className="inline w-3 h-3 mr-0.5 -mt-0.5" />Shared slip total {d.pool_total} · {d.pool_customers} customers (file {d.pool_file_total})
        </span>
      )}
    </div>
  );
};

export const QtyMismatchDialog = ({ mismatch, onClose, onRepaired }) => {
  const [qtys, setQtys] = useState({});
  const [busy, setBusy] = useState(false);
  const [items, setItemsRaw] = useState([]);
  const kind = mismatch?.kind || "bulk";
  const entryId = mismatch?.entryId;

  const setItems = (list) => {
    setItemsRaw(list);
    const init = {};
    list.forEach((d, i) => { init[i] = String(d.source_qty ?? ""); });
    setQtys(init);
  };
  useEffect(() => {
    setItems(mismatch?.items || []);
    // eslint-disable-next-line
  }, [mismatch]);

  const links = useSlipNameLinks(kind === "pl" ? entryId : null, (discs) => {
    setItems(discs);
    onRepaired?.({ qty_discrepancies: discs, relinked: true });
    if (!discs.length) toast.success("All products now match the slip");
  });

  if (!mismatch) return null;

  const repair = async () => {
    const corrections = items.map((d, i) => ({
      customer: d.customer, product: d.product,
      qty: qtys[i] === "" ? d.source_qty : qtys[i],
    }));
    setBusy(true);
    try {
      const base = kind === "pl" ? "packing-list" : "bulk-data";
      const r = await axios.post(`${API}/${base}/history/${entryId}/repair-qty`,
        { corrections }, { headers: getAuthHeader() });
      const left = (r.data.qty_discrepancies || []).length;
      toast.success(left === 0
        ? "Quantities fixed — file regenerated, no mismatches left"
        : `File regenerated. ${left} mismatch(es) still remain.`);
      onRepaired?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repair failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!mismatch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="qty-mismatch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-4 h-4" /> Quantity differences — {items.length} product(s)
          </DialogTitle>
          <DialogDescription>
            Order Sheet quantity differs from the Packing Slip. Type the correct quantity
            (default = slip qty) and repair — the file is regenerated with fixed quantities.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Product</th>
                <th className="text-center p-2">Slip Qty</th>
                <th className="text-center p-2">File Qty</th>
                <th className="text-center p-2">Diff</th>
                <th className="text-center p-2 w-24">Correct Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d, i) => (
                <tr key={i} className="border-t" data-testid={`qty-mismatch-row-${i}`}>
                  <td className="p-2 font-medium">{d.customer}</td>
                  <td className="p-2">
                    <div>{d.product}</div>
                    <MatchHints d={d} i={i} />
                    {kind === "pl" && d.slip_product === "" && links.slipNames.length > 0 && (
                      <LinkSlipSelect d={d} i={i} slipNames={links.slipNames} onLink={links.link} />
                    )}
                  </td>
                  <td className="p-2 text-center">{d.source_qty}</td>
                  <td className="p-2 text-center">{d.output_qty}</td>
                  <td className="p-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      d.delta > 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}
                      data-testid={`qty-mismatch-delta-${i}`}>
                      {d.delta > 0 ? `+${d.delta} more` : `${d.delta} less`}
                    </span>
                  </td>
                  <td className="p-1 text-center">
                    <Input type="number" min="0" className="h-7 w-20 text-xs mx-auto text-center"
                      value={qtys[i] ?? ""} data-testid={`qty-mismatch-input-${i}`}
                      onChange={(e) => setQtys({ ...qtys, [i]: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {kind === "pl" && <SavedLinksList aliases={links.aliases} onUnlink={links.unlink} />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="qty-mismatch-close">Close</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy}
                  onClick={repair} data-testid="qty-mismatch-repair">
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
            Repair & Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
